/**
 * What the leads page says first: who to write to, who came back, whether
 * leads are growing, and what stops the vendor reaching them.
 */
import type { Lead } from '../../../../common/hooks/use-leads-api.hook';
import type { LeadPipeline } from './lead-temperature';
import { VIZ_LINKS } from './links';
import { ReadingItem, changePercent, sharePercent } from './reading';
import type { Pair } from './viz-reading';

/** The product most of these leads looked at, if one leads by a clear margin (and at least three leads). */
export function commonProduct(leads: Lead[]): { name: string; count: number } | null {
  const counts = new Map<string, { name: string; count: number }>();
  for (const lead of leads) {
    const seen = new Set<string>();
    for (const p of lead.products ?? []) {
      if (!p?.productId || seen.has(p.productId)) continue;
      seen.add(p.productId);
      const entry = counts.get(p.productId) ?? { name: p.title || p.productId, count: 0 };
      entry.count += 1;
      counts.set(p.productId, entry);
    }
  }
  const [first, second] = Array.from(counts.values()).sort((a, b) => b.count - a.count);
  if (!first || first.count < 3) return null;
  if (second && second.count === first.count) return null;
  return first;
}

export function leadsReading(input: {
  pipeline: LeadPipeline;
  hotWaiting: number;
  newLeads: Pair;
  locked?: number;
  topProduct?: { name: string; count: number } | null;
}): ReadingItem[] {
  const { pipeline } = input;
  const items: ReadingItem[] = [];

  if (input.hotWaiting > 0) {
    items.push({
      id: 'hot-waiting',
      tone: 'watch',
      title: { key: 'HOT_LEADS_WAITING', params: { count: input.hotWaiting } },
      detail: { key: 'LEADS_WAITING_DETAIL' },
      action: { key: 'SHOW_WAITING', href: `${VIZ_LINKS.leads}?filter=waiting` },
    });
  } else if (pipeline.waiting > 0) {
    items.push({
      id: 'waiting',
      tone: 'watch',
      title: { key: 'LEADS_WAITING', params: { count: pipeline.waiting } },
      detail: { key: 'LEADS_WAITING_DETAIL' },
      action: { key: 'SHOW_WAITING', href: `${VIZ_LINKS.leads}?filter=waiting` },
    });
  }

  if (pipeline.returned > 0) {
    items.push({
      id: 'returned',
      tone: 'good',
      title: { key: 'LEADS_RETURNED', params: { count: pipeline.returned } },
      detail: { key: 'LEADS_RETURNED_DETAIL' },
      action: { key: 'SHOW_WAITING', href: `${VIZ_LINKS.leads}?filter=waiting` },
    });
  }

  const n = input.newLeads.value ?? 0;
  const np = input.newLeads.previous ?? 0;
  const change = changePercent(n, np);
  if (n > 0 && !np) items.push({ id: 'new', tone: 'good', title: { key: 'LEADS_NEW', params: { count: n } } });
  else if (change != null && change >= 20) items.push({ id: 'new', tone: 'good', title: { key: 'LEADS_UP', params: { count: n, percent: change } } });
  else if (change != null && change <= -20) items.push({ id: 'new', tone: 'bad', title: { key: 'LEADS_DOWN', params: { count: n, percent: Math.abs(change) } }, detail: { key: 'LEADS_DOWN_DETAIL' } });
  else if (n > 0) items.push({ id: 'new', tone: 'neutral', title: { key: 'LEADS_STEADY', params: { count: n } } });

  const consent = sharePercent(pipeline.consented, pipeline.total, 10);
  if (consent != null && consent < 30) {
    items.push({
      id: 'consent',
      tone: 'watch',
      title: { key: 'LOW_CONSENT', params: { percent: consent } },
      detail: { key: 'LOW_CONSENT_DETAIL' },
      action: { key: 'FEATURES', href: VIZ_LINKS.features },
    });
  }

  if (input.locked && input.locked > 0) {
    items.push({
      id: 'locked',
      tone: 'bad',
      title: { key: 'LEADS_LOCKED', params: { count: input.locked } },
      action: { key: 'SEE_PLANS', href: VIZ_LINKS.plans },
    });
  }

  if (input.topProduct) {
    items.push({ id: 'product', tone: 'neutral', title: { key: 'LEADS_TOP_PRODUCT', params: { name: input.topProduct.name, count: input.topProduct.count } } });
  }

  return items.slice(0, 5);
}
